/**
 * ChartBaseEditor
 *
 * Base component for all chart property editors
 * Provides shared configuration UI for datasource, series, axes, legend, and styling
 * Composite Pattern: Composes multiple configurators for modular chart setup
 */

import React, { useCallback, useState } from 'react';
import { TextPropertyEditor } from '../../BaseEditors/TextPropertyEditor';
import { SelectPropertyEditor, type SelectOption } from '../../BaseEditors/SelectPropertyEditor';
import './ChartBaseEditor.css';
import { ChevronDownIcon, ChevronRightIcon } from '@atlas-forms/ui-components-react';

/** Chart series data configuration */
export interface ChartSeries {
  /** Unique identifier for the series */
  id: string;
  /** Series name/label */
  name: string;
  /** Data key from datasource (e.g., 'value', 'revenue') */
  dataKey: string;
  /** Series color (hex or named) */
  color?: string;
  /** Series type (line, area, bar, etc.) */
  type?: string;
  /** Additional series-specific options */
  options?: Record<string, unknown>;
}

/** Chart axis configuration */
export interface ChartAxis {
  /** Axis type: 'x' or 'y' */
  type: 'x' | 'y';
  /** Axis label */
  label?: string;
  /** Data key for axis (for X axis in scatter/bubble) */
  dataKey?: string;
  /** Show grid */
  showGrid?: boolean;
  /** Custom tick interval */
  tickInterval?: number;
  /** Axis scale type: 'linear', 'logarithmic' */
  scaleType?: 'linear' | 'logarithmic';
}

/** Chart legend configuration */
export interface ChartLegendConfig {
  /** Show legend */
  visible?: boolean;
  /** Legend position: 'top', 'right', 'bottom', 'left' */
  position?: 'top' | 'right' | 'bottom' | 'left';
  /** Legend alignment */
  align?: 'left' | 'center' | 'right';
}

/** Base chart configuration shared across all chart types */
export interface ChartBaseConfig {
  /** Datasource binding (field name or data URL) */
  datasource: string;
  /** Chart series configuration */
  series: ChartSeries[];
  /** Chart axes (for applicable chart types) */
  axes?: ChartAxis[];
  /** Legend configuration */
  legend?: ChartLegendConfig;
  /** Chart title */
  title?: string;
  /** Chart description/subtitle */
  description?: string;
  /** Chart aspect ratio/height */
  height?: number;
  /** Responsive: scale to container width */
  responsive?: boolean;
  /** Margin around chart (px) */
  margin?: { top: number; right: number; bottom: number; left: number };
  /** Manually entered data rows (for manual data source mode) */
  staticData?: Array<{ name: string; value: number }>;
  /** Data source type: 'manual' = static data, 'api' = fetch from endpoint */
  dataSourceType?: 'manual' | 'api';
  /** API endpoint URL for fetching chart data */
  apiEndpoint?: string;
  /** Maps API response fields to chart x/y axes */
  apiFieldMapping?: { xField: string; yField: string };
  /** Auto-refresh interval in milliseconds (0 or undefined = disabled) */
  refreshInterval?: number;
  /** Allow chart-specific config properties */
  [key: string]: unknown;
}

export interface ChartBaseEditorProps {
  /** Current chart configuration */
  config: Partial<ChartBaseConfig>;
  /** Called when configuration changes */
  onChange: (config: Partial<ChartBaseConfig>) => void;
  /** Chart control type (for labeling) */
  chartType?: string;
  /** Whether to show axes configuration (hide for pie, tree-map, etc.) */
  showAxes?: boolean;
  /** Whether to show series type selector */
  showSeriesType?: boolean;
  /** Custom series type options */
  seriesTypeOptions?: SelectOption[];
  /** Axes configurator component (shown in Axes Configuration section) */
  axesConfigurator?: React.ReactNode;
  /** Additional children for chart-specific options */
  children?: React.ReactNode;
}

/**
 * ChartBaseEditor component
 * Provides composite configuration for chart controls
 */
export const ChartBaseEditor: React.FC<ChartBaseEditorProps> = ({
  config,
  onChange,
  chartType = 'Chart',
  showAxes = true,
  showSeriesType = false,
  seriesTypeOptions = [],
  axesConfigurator,
  children,
}) => {
  const [expandedSeries, setExpandedSeries] = useState<string | null>(null);

  // Handle datasource change
  const handleDatasourceChange = useCallback(
    (datasource: string) => {
      onChange({ ...config, datasource });
    },
    [config, onChange],
  );

  // Handle title change
  const handleTitleChange = useCallback(
    (title: string) => {
      onChange({ ...config, title });
    },
    [config, onChange],
  );

  // Handle series name change
  const handleSeriesNameChange = useCallback(
    (seriesId: string, name: string) => {
      const updatedSeries = (config.series || []).map((s) =>
        s.id === seriesId ? { ...s, name } : s,
      );
      onChange({ ...config, series: updatedSeries });
    },
    [config, onChange],
  );

  // Handle series data key change
  const handleSeriesDataKeyChange = useCallback(
    (seriesId: string, dataKey: string) => {
      const updatedSeries = (config.series || []).map((s) =>
        s.id === seriesId ? { ...s, dataKey } : s,
      );
      onChange({ ...config, series: updatedSeries });
    },
    [config, onChange],
  );

  // Handle series color change
  const handleSeriesColorChange = useCallback(
    (seriesId: string, color: string) => {
      const updatedSeries = (config.series || []).map((s) =>
        s.id === seriesId ? { ...s, color } : s,
      );
      onChange({ ...config, series: updatedSeries });
    },
    [config, onChange],
  );

  // Handle adding series
  const handleAddSeries = useCallback(() => {
    const newSeries: ChartSeries = {
      id: `series-${Date.now()}`,
      name: `Series ${(config.series || []).length + 1}`,
      dataKey: '',
      color: '#8884d8',
    };
    onChange({ ...config, series: [...(config.series || []), newSeries] });
  }, [config, onChange]);

  // Handle removing series
  const handleRemoveSeries = useCallback(
    (seriesId: string) => {
      const updatedSeries = (config.series || []).filter((s) => s.id !== seriesId);
      onChange({ ...config, series: updatedSeries });
    },
    [config, onChange],
  );

  // Handle height change
  const handleHeightChange = useCallback(
    (height: number) => {
      onChange({ ...config, height });
    },
    [config, onChange],
  );

  // Handle legend visibility change
  const handleLegendVisibleChange = useCallback(
    (visible: boolean) => {
      onChange({
        ...config,
        legend: { ...config.legend, visible },
      });
    },
    [config, onChange],
  );

  // Handle adding a static data row
  const handleAddDataRow = useCallback(() => {
    const newRow = { name: `Item ${(config.staticData || []).length + 1}`, value: 0 };
    onChange({ ...config, staticData: [...(config.staticData || []), newRow] });
  }, [config, onChange]);

  // Handle updating a static data row
  const handleUpdateDataRow = useCallback(
    (index: number, field: 'name' | 'value', newValue: string | number) => {
      const updated = [...(config.staticData || [])];
      if (field === 'name') {
        updated[index] = { ...updated[index], name: newValue as string };
      } else {
        updated[index] = { ...updated[index], value: Number(newValue) || 0 };
      }
      onChange({ ...config, staticData: updated });
    },
    [config, onChange],
  );

  // Handle removing a static data row
  const handleRemoveDataRow = useCallback(
    (index: number) => {
      const updated = (config.staticData || []).filter((_, i) => i !== index);
      onChange({ ...config, staticData: updated });
    },
    [config, onChange],
  );

  // Handle data source type change
  const handleDataSourceTypeChange = useCallback(
    (dataSourceType: string | number) => {
      onChange({ ...config, dataSourceType: dataSourceType as 'manual' | 'api' });
    },
    [config, onChange],
  );

  // Handle API endpoint change
  const handleApiEndpointChange = useCallback(
    (apiEndpoint: string) => {
      onChange({ ...config, apiEndpoint });
    },
    [config, onChange],
  );

  // Handle API X-axis field change
  const handleApiXFieldChange = useCallback(
    (xField: string) => {
      onChange({
        ...config,
        apiFieldMapping: { xField, yField: config.apiFieldMapping?.yField ?? '' },
      });
    },
    [config, onChange],
  );

  // Handle API Y-axis field change
  const handleApiYFieldChange = useCallback(
    (yField: string) => {
      onChange({
        ...config,
        apiFieldMapping: { xField: config.apiFieldMapping?.xField ?? '', yField },
      });
    },
    [config, onChange],
  );

  // Handle refresh interval change
  const handleRefreshIntervalChange = useCallback(
    (refreshInterval: string) => {
      onChange({ ...config, refreshInterval: Number(refreshInterval) || 0 });
    },
    [config, onChange],
  );

  const series = config.series || [];
  const title = config.title || '';
  const height = config.height || 400;
  const legendVisible = config.legend?.visible !== false;
  const dataSourceType = config.dataSourceType ?? 'manual';

  return (
    <div className="chart-editor">
      <div className="chart-editor__section">
        <h3 className="chart-editor__section-title">Data Configuration</h3>

        <TextPropertyEditor
          label="Datasource"
          value={config.datasource || ''}
          onChange={handleDatasourceChange}
          placeholder="Field name or data URL"
          helpText="Bind to a form field containing array data"
          required
        />

        <TextPropertyEditor
          label={`${chartType} Title`}
          value={title}
          onChange={handleTitleChange}
          placeholder="Enter chart title"
          helpText="Optional title displayed above chart"
        />

        <div className="chart-editor__number-input">
          <label className="af-property-editor__label">Height (px)</label>
          <input
            type="number"
            min="200"
            max="1000"
            step="50"
            value={height}
            onChange={(e) => handleHeightChange(Number(e.target.value))}
            className="af-property-editor__input"
          />
        </div>

        {/* Data Source Type toggle */}
        <SelectPropertyEditor
          label="Data Source Type"
          value={dataSourceType}
          onChange={handleDataSourceTypeChange}
          options={[
            { value: 'manual', label: 'Manual / Form Binding' },
            { value: 'api', label: 'API Endpoint' },
          ]}
          helpText="Choose where this chart gets its data"
        />

        {/* Manual Data Entry — only shown when dataSourceType === 'manual' */}
        {dataSourceType !== 'api' && (
          <div className="chart-editor__data-entry">
            <div>
              <h4>Manual Data Rows</h4>
              <p>Enter data that will display in the chart</p>
            </div>

            {(config.staticData || []).length === 0 ? (
              <div className="chart-editor__data-entry-empty">
                No data rows yet. Click "Add Row" to start entering data.
              </div>
            ) : (
              <table className="chart-editor__data-table">
                <thead>
                  <tr>
                    <th>Name / X-Axis</th>
                    <th>Value / Y-Axis</th>
                    <th className="chart-editor__data-action-col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(config.staticData || []).map((row, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          type="text"
                          value={row.name}
                          onChange={(e) => handleUpdateDataRow(idx, 'name', e.target.value)}
                          className="chart-editor__data-input"
                          placeholder="e.g. Jan, Category A"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row.value}
                          onChange={(e) => handleUpdateDataRow(idx, 'value', e.target.value)}
                          className="chart-editor__data-input"
                          placeholder="0"
                        />
                      </td>
                      <td className="chart-editor__data-action-col">
                        <button
                          onClick={() => handleRemoveDataRow(idx)}
                          className="chart-editor__delete-button"
                          title="Delete row"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <button
              onClick={handleAddDataRow}
              className="chart-editor__add-row-button"
            >
              + Add Row
            </button>
          </div>
        )}

        {/* API Configuration — only shown when dataSourceType === 'api' */}
        {dataSourceType === 'api' && (
          <div className="chart-editor__api-config">
            <TextPropertyEditor
              label="API Endpoint URL"
              value={config.apiEndpoint || ''}
              onChange={handleApiEndpointChange}
              placeholder="https://api.example.com/data"
              helpText="GET request. Must return a JSON array."
            />

            <TextPropertyEditor
              label="X-Axis Field"
              value={config.apiFieldMapping?.xField || ''}
              onChange={handleApiXFieldChange}
              placeholder="e.g. date, category, name"
              helpText="Property name in each response object to use as X axis / label"
            />

            <TextPropertyEditor
              label="Y-Axis Field"
              value={config.apiFieldMapping?.yField || ''}
              onChange={handleApiYFieldChange}
              placeholder="e.g. value, revenue, count"
              helpText="Property name in each response object to use as Y axis / value"
            />

            <div className="chart-editor__number-input">
              <label className="af-property-editor__label">Auto-Refresh Interval (ms)</label>
              <input
                type="number"
                min="0"
                max="3600000"
                step="1000"
                value={config.refreshInterval ?? 0}
                onChange={(e) => handleRefreshIntervalChange(e.target.value)}
                className="af-property-editor__input"
              />
              <span className="af-property-editor__help">0 = disabled. Min recommended: 5000</span>
            </div>
          </div>
        )}
      </div>

      <div className="chart-editor__section">
        <div className="chart-editor__section-header">
          <h3 className="chart-editor__section-title">Series Configuration</h3>
          <button
            onClick={handleAddSeries}
            className="chart-editor__add-button"
            aria-label="Add new series"
          >
            + Add Series
          </button>
        </div>

        {series.length === 0 ? (
          <div className="chart-editor__empty">
            <p>No series configured. Click "Add Series" to create one.</p>
          </div>
        ) : (
          <div className="chart-editor__series-list">
            {series.map((ser) => (
              <div key={ser.id} className="chart-editor__series-item">
                <button
                  className="chart-editor__series-toggle"
                  onClick={() =>
                    setExpandedSeries(expandedSeries === ser.id ? null : ser.id)
                  }
                  aria-expanded={expandedSeries === ser.id}
                >
                  {expandedSeries === ser.id ? <ChevronDownIcon width={12} height={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> : <ChevronRightIcon width={12} height={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />} {ser.name}
                </button>

                {expandedSeries === ser.id && (
                  <div className="chart-editor__series-config">
                    <TextPropertyEditor
                      label="Series Name"
                      value={ser.name}
                      onChange={(name) => handleSeriesNameChange(ser.id, name)}
                      placeholder="e.g., Revenue"
                      required
                    />

                    <TextPropertyEditor
                      label="Data Key"
                      value={ser.dataKey}
                      onChange={(dataKey) => handleSeriesDataKeyChange(ser.id, dataKey)}
                      placeholder="e.g., value, revenue"
                      helpText="Property name from data objects"
                      required
                    />

                    <div className="chart-editor__color-input">
                      <label className="af-property-editor__label">Color</label>
                      <input
                        type="color"
                        value={ser.color || '#8884d8'}
                        onChange={(e) => handleSeriesColorChange(ser.id, e.target.value)}
                        className="chart-editor__color-picker"
                      />
                    </div>

                    {showSeriesType && seriesTypeOptions.length > 0 && (
                      <SelectPropertyEditor
                        label="Series Type"
                        value={ser.type || ''}
                        onChange={(type) => {
                          const updated = (config.series || []).map((s) =>
                            s.id === ser.id ? { ...s, type: type as string } : s,
                          );
                          onChange({ ...config, series: updated });
                        }}
                        options={seriesTypeOptions}
                        placeholder="Select series type"
                      />
                    )}

                    <button
                      onClick={() => handleRemoveSeries(ser.id)}
                      className="chart-editor__remove-button"
                      aria-label={`Remove series ${ser.name}`}
                    >
                      Remove Series
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showAxes && (
        <div className="chart-editor__section">
          <h3 className="chart-editor__section-title">Axes Configuration</h3>
          <p className="chart-editor__section-help">
            Configure X and Y axes for your chart
          </p>
          {axesConfigurator ? (
            axesConfigurator
          ) : (
            <p className="chart-editor__section-help">No axis configuration available for this chart type</p>
          )}
        </div>
      )}

      <div className="chart-editor__section">
        <h3 className="chart-editor__section-title">Legend</h3>
        <div className="chart-editor__checkbox">
          <input
            type="checkbox"
            id="legend-visible"
            checked={legendVisible}
            onChange={(e) => handleLegendVisibleChange(e.target.checked)}
            className="af-property-editor__checkbox"
          />
          <label htmlFor="legend-visible" className="af-property-editor__label">
            Show Legend
          </label>
        </div>

        {legendVisible && (
          <SelectPropertyEditor
            label="Legend Position"
            value={config.legend?.position || 'right'}
            onChange={(position) => {
              onChange({
                ...config,
                legend: { ...config.legend, position: position as 'top' | 'right' | 'bottom' | 'left' },
              });
            }}
            options={[
              { value: 'top', label: 'Top' },
              { value: 'right', label: 'Right' },
              { value: 'bottom', label: 'Bottom' },
              { value: 'left', label: 'Left' },
            ]}
          />
        )}
      </div>

      {children && <div className="chart-editor__section">{children}</div>}
    </div>
  );
};

ChartBaseEditor.displayName = 'ChartBaseEditor';
